import { testimonialCoach } from '@/lib/testimonialClient';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ClipboardList, FileSignature, Loader2, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import CoachAkteUebersicht, { AkteKopfCoach } from '@/components/CoachAkteUebersicht';
import AngebotErstellen from '@/components/kundenakte/AngebotErstellen';
import { datumKurz } from '@/components/kundenakte/akteText';
import { CheckupStepperPanel } from '@/components/CheckupStepper';
import CoachChat from '@/components/CoachChat';
import CoachingCheckins from '@/components/CoachingCheckins';
import CoachingKonversation from '@/components/CoachingKonversation';
import KundeEntwicklung from '@/components/KundeEntwicklung';
import KundeFortschritt, { coachFortschrittClient } from '@/components/KundeFortschritt';
import { CheckinRhythmusKarte } from '@/components/CheckinRhythmusKarte';
import CoachCheckins, { rhythmusClient } from '@/pages/CoachCheckins';
import { setErnaehrungClient, clearErnaehrungClient } from '@/lib/ernaehrungClient';
import { MitgliedFormulare, useMitgliedDaten } from '@/pages/CoachErnaehrung';
import { KundenSeite } from '@/pages/CoachErnaehrungKunden';
import { Hinweis, KarteSchutz, LinkUmleitung, fehlertext } from '@/pages/CoachErnaehrungTeile';
import { PersonenPlaene, PlanNeu } from '@/components/PlanEditor2';

const PLANER = '/planer';   // Trainingsplan-Editor auf /coach (W3.1, `/api/coach/planer`) — Rechte prüft der Server

/* Kundenakte auf der Trainer-Webseite /coach (29.10.2026, Coach-Cockpit W1.4, Entscheidung E4): `/coach/kunden/:id` bzw.
   `/coach?ansicht=kunde&kunde=<lead>&reiter=…`. Dieselbe Akte wie `/leads/:id` im CRM (GET /api/coach/kunden/{id}/akte ruft
   serverseitig `kundenakte.akte`), dieselben Karten (AkteKopf, AkteUebersicht, AngebotErstellen, Check-up-Stepper) — nur
   über den Trainer-Client. Die Athlet-Ansicht (Kraftlog) und die Ernährungs-Kundenseite sind Reiter dieser Akte; ihre alten
   Adressen (`athlet=`, `mitglied=`) leitet CoachPage über `umleiten` hierher. Rechte entscheidet der Server (`zugang`):
   ohne Ernährungs-Freigabe gibt es keine Messwerte und die Ernährungs-Reiter stehen als Satz da. */

export const REITER = [
  ['uebersicht', 'Übersicht'], ['training', 'Training'], ['ernaehrung', 'Ernährung'], ['checkins', 'Check-ins'],
  ['fortschritt', 'Fortschritt'], ['chat', 'Chat'], ['vertraege', 'Verträge & Angebote'], ['profil', 'Profil'],
];

/* Reiter-Namen anderer Pakete (Coach-Heute, Kundeninfo): Ziele und Phase stehen in der Übersicht, Angebote unter
   „Verträge & Angebote“ (Integration W2, 29.10.2026 — dieselbe Abbildung wie `coach/heute.py` REITER_ALIAS). */
export const REITER_ALIAS = { ziele: 'uebersicht', phase: 'uebersicht', angebote: 'vertraege' };

/* Alte Wege → Akte. `athlet=` (Athleten, Bestenliste, P1 Coaching) öffnet den Reiter Training, `mitglied=` (Ernährung ›
   Kunden, Start, Cockpit „Kundenseite“/„Plan“, Links der CRM-Karten) den Reiter Ernährung mit seinem Unterreiter. */
export function umleiten(p) {
  if (!p) return p;
  if (p.ansicht === 'kunde' && p.reiter && REITER_ALIAS[p.reiter]) return { ...p, reiter: REITER_ALIAS[p.reiter] };
  if ((p.ansicht === 'athleten' || !p.ansicht) && p.athlet) return { ansicht: 'kunde', kunde: p.athlet, reiter: 'training' };
  if (p.ansicht === 'e-kunden' && p.mitglied) return { ansicht: 'kunde', kunde: p.mitglied, reiter: 'ernaehrung', ...(p.reiter ? { ereiter: p.reiter } : {}) };
  return p;
}

/* Route `/coach/kunden/:id` (App.js): öffnet CoachPage mit der Akte; `?reiter=` (oder `?tab=` wie im CRM) geht mit. */
export function CoachKundeUmleitung() {
  const { id } = useParams();
  const { search } = useLocation();
  const q = new URLSearchParams(search);
  if (q.has('tab') && !q.has('reiter')) q.set('reiter', q.get('tab'));
  q.delete('tab');
  q.set('ansicht', 'kunde');
  q.set('kunde', id);
  return <Navigate to={`/coach?${q.toString()}`} replace />;
}

/* Check-up-Stepper über die Trainer-Sitzung: dieselben Aufrufe wie `crmClient` (CheckupStepper.js), Adressen
   `/api/coach/checkup-ablauf/{id}/…` (Spiegel der CRM-Routen, coach/kundenakte.py). */
export function coachCheckupClient(coach, id) {
  const p = (rest) => `/checkup-ablauf/${id}/${rest}`;
  return {
    ablauf: () => coach.get(p('ablauf')),
    einwilligung: (d) => coach.post(p('einwilligung'), d),
    messung: (code) => coach.post(p('messung'), { code }),
    messwerte: (d) => coach.put(p('messwerte'), d),
    antworten: (antworten) => coach.put(p('antworten'), { antworten }),
    kurse: (termine) => coach.post(p('kurse'), { termine }),
    befund: (d) => coach.put(p('befund'), d),
    empfehlung: () => coach.post(p('empfehlung'), {}),
    abschluss: (d) => coach.post(p('abschluss'), d, { timeout: 60000 }),
    art: (art) => coach.put(p('art'), { art }),
    // w1-ergebnis: Ergebnis-/Abschluss-Check-up über denselben Spiegel
    ergebnis: () => coach.get(p('ergebnis')),
    einschaetzungVorschlag: () => coach.post(p('einschaetzung/vorschlag'), {}),
    einschaetzung: (d) => coach.put(p('einschaetzung'), d),
    weg: (d) => coach.put(p('weg'), d),
    foto: (fid) => coach.get(p(`ergebnis/foto/${fid}`), { responseType: 'blob' }),
    pdf: () => coach.get(p('ergebnis/pdf'), { responseType: 'blob', timeout: 60000 }),
  };
}

const chipText = (c) => (typeof c === 'string' ? c : (c && typeof c.text === 'string' ? c.text : null));

/* Bausteine-Chips aus einem anderen Paket (`kopf.chips`, z. B. „Beschleunigungsphase · Woche 3“, „PT 2/4“) — optional. */
function Chips({ chips }) {
  const liste = (Array.isArray(chips) ? chips : []).map(chipText).filter(Boolean);
  if (!liste.length) return null;
  return (
    <>
      {liste.map((t, i) => <span key={`${t}-${i}`} className="inline-flex items-center rounded-full border border-border bg-muted px-[11px] py-[5px] text-xs font-bold">{t}</span>)}
    </>
  );
}

/* „Plan eingetragen %“ aus der Athlet-Sicht (Planerfüllung vier Wochen) — nur, wenn die Person im Trainingsbereich liegt. */
function usePlanerfuellung(coach, leadId) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    let weg = false;
    setPlan(null);
    coach.get(`/athleten/${leadId}`).then((r) => { if (!weg) setPlan(r.data?.plan || null); }).catch(() => { if (!weg) setPlan(null); });
    return () => { weg = true; };
  }, [coach, leadId]);
  return plan;
}

function Formulare({ coach, mid }) {
  const { d, err, laden } = useMitgliedDaten(coach, mid);
  if (err && !d) return <Hinweis>{err}</Hinweis>;
  if (!d) return <Skeleton className="h-32 w-full rounded-[14px]" />;
  return <MitgliedFormulare coach={coach} mid={mid} d={d} laden={laden} />;
}

const euro = (n) => `${Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const STATUS = { active: 'aktiv', paused: 'pausiert', cancelled: 'gekündigt', offen: 'offen', bezahlt: 'bezahlt', laufend: 'läuft',
  abgeschlossen: 'abgeschlossen', verfallen: 'verfallen', angenommen: 'angenommen', abgelehnt: 'abgelehnt', abgelaufen: 'abgelaufen' };

function Liste({ titel, leer, children, rechts }) {
  return (
    <section className="space-y-2 rounded-[14px] border border-border/70 bg-card p-4">
      <div className="flex items-center gap-2"><h3 className="text-sm font-bold">{titel}</h3>{rechts && <div className="ml-auto">{rechts}</div>}</div>
      {children || <p className="text-xs text-muted-foreground">{leer}</p>}
    </section>
  );
}

function Vertraege({ coach, leadId, onAngebot }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { coach.get(`/kunden/${leadId}/vertraege`).then((r) => setD(r.data)).catch((e) => setErr(fehlertext(e, 'Verträge nicht geladen'))); }, [coach, leadId]);
  if (err) return <Hinweis>{err}</Hinweis>;
  if (!d) return <Skeleton className="h-40 w-full rounded-[14px]" />;
  const zeile = (k, links, rechts) => <li key={k} className="flex flex-wrap justify-between gap-2 py-1.5 text-sm"><span>{links}</span><span className="text-xs text-muted-foreground tabular-nums">{rechts}</span></li>;
  return (
    <div className="space-y-4" data-testid="coach-akte-vertraege">
      <Liste titel="Mitgliedschaften" leer="Keine Mitgliedschaft.">
        {d.mitgliedschaften.length > 0 && <ul className="divide-y divide-border/60">{d.mitgliedschaften.map((m) => zeile(m.id || m.von,
          <><b>{m.tarif || 'Mitgliedschaft'}</b>{m.zusatz?.length ? <span className="text-muted-foreground"> · {m.zusatz.join(', ')}</span> : null}</>,
          `${STATUS[m.status] || m.status || ''} · ab ${datumKurz(m.von)}${m.bis ? ` · Mindestlaufzeit bis ${datumKurz(m.bis)}` : ''}`))}</ul>}
      </Liste>
      <Liste titel="P1 Coaching" leer="Kein Coaching-Paket.">
        {d.pakete.length > 0 && <ul className="divide-y divide-border/60">{d.pakete.map((p) => zeile(p.id,
          <><b>{p.titel}</b>{p.coach ? <span className="text-muted-foreground"> · {p.coach}</span> : null}</>,
          `${STATUS[p.status] || p.status} · ${p.genutzt} von ${p.soll} Terminen${p.von ? ` · ${datumKurz(p.von)}–${datumKurz(p.bis)}` : ''}`))}</ul>}
      </Liste>
      <Liste titel="Angebote" leer="Noch kein Angebot." rechts={<Button size="sm" className="min-h-11 gap-1.5 bg-marke text-white hover:bg-marke-dunkel" onClick={onAngebot} data-testid="coach-akte-angebot-neu"><FileSignature className="h-4 w-4" /> Angebot erstellen</Button>}>
        {d.angebote.length > 0 && <ul className="divide-y divide-border/60">{d.angebote.map((a) => zeile(a.id,
          <><b>{a.titel}</b><span className="text-muted-foreground"> · {STATUS[a.status] || a.status}</span></>,
          `${a.summen?.einmalig ? `${euro(a.summen.einmalig)} einmalig` : ''}${a.summen?.einmalig && a.summen?.monatlich ? ' + ' : ''}${a.summen?.monatlich ? `${euro(a.summen.monatlich)} je Monat` : ''} · vom ${datumKurz(a.erstellt_am)}${a.status === 'offen' ? ` · gilt bis ${datumKurz(a.gueltig_bis)}` : ''}`))}</ul>}
      </Liste>
      <Hinweis>Beiträge, Bankdaten und Verträge pflegt das Studio im CRM.</Hinweis>
    </div>
  );
}

function Profil({ coach, leadId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { coach.get(`/kunden/${leadId}/profil`).then((r) => setD(r.data)).catch((e) => setErr(fehlertext(e, 'Profil nicht geladen'))); }, [coach, leadId]);
  if (err) return <Hinweis>{err}</Hinweis>;
  if (!d) return <Skeleton className="h-40 w-full rounded-[14px]" />;
  const zeilen = [
    ['Name', d.name], ['Alter', d.alter != null ? `${d.alter} Jahre` : null], ['Geburtstag', d.geburtstag], ['Geschlecht', d.geschlecht],
    ['Mitgliedsnummer', d.mitgliedsnummer], ['Stufe', d.stufe], ['Feste Kurse', d.stammkurse?.length ? d.stammkurse.join(', ') : null],
    ['Fotos/Videos', d.keine_fotos ? 'Einwilligung widerrufen – keine Fotos' : null], ['Im System seit', d.seit ? datumKurz(d.seit) : null],
  ];
  return (
    <section className="space-y-3 rounded-[14px] border border-border/70 bg-card p-4" data-testid="coach-akte-profil">
      <dl className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-4 gap-y-2 text-sm">
        {zeilen.map(([t, w]) => (<div key={t} className="contents"><dt className="text-muted-foreground">{t}</dt><dd className={`font-medium ${t === 'Fotos/Videos' && w ? 'text-amber-600 dark:text-amber-400' : ''}`}>{w ?? '–'}</dd></div>))}
      </dl>
      <Hinweis>Kontaktdaten, Adresse und Einwilligungen pflegt das Studio im CRM.</Hinweis>
    </section>
  );
}

export default function CoachKundeAkte({ coach, ich, leadId, reiter, ereiter, checkin, stand, gehe, Training }) {
  const navigate = useNavigate();
  // Ernährungs-Karten (Entwicklung, Protokoll, Plan, Konversation) laufen über den Spiegel /api/coach/x — erst umschalten, dann
  // einhängen (wie CoachErnaehrungBereich). Ohne Freigabe am Zugang bleibt der Client, wie er ist.
  const ernZugang = !!(ich?.ernaehrung || ich?.master);
  const [bereit, setBereit] = useState(!ernZugang);
  useEffect(() => {
    if (!ernZugang) return undefined;
    setErnaehrungClient(coach, '/x');
    setBereit(true);
    return () => { clearErnaehrungClient(); };
  }, [coach, ernZugang]);

  const [akte, setAkte] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [stepper, setStepper] = useState(null);
  const [angebotOffen, setAngebotOffen] = useState(false);
  const plan = usePlanerfuellung(coach, leadId);
  const laden = useCallback(async () => {
    try { setAkte((await coach.get(`/kunden/${leadId}/akte`)).data); setFehler(null); }
    catch (e) { setAkte(null); setFehler(fehlertext(e, 'Kundenakte nicht geladen')); }
  }, [coach, leadId]);
  useEffect(() => { setAkte(null); setFehler(null); laden(); }, [laden]);

  const aktiv = REITER.some(([id]) => id === reiter) ? reiter : 'uebersicht';
  const wechsel = (id, extra = {}) => gehe({ ansicht: 'kunde', kunde: leadId, reiter: id, ...extra });
  const zugang = akte?.zugang || {};
  const mitErnaehrung = !!(zugang.ernaehrung && bereit);
  const stepperClient = useMemo(() => (stepper ? { ...coachCheckupClient(coach, stepper), testimonial: testimonialCoach(coach, leadId) } : null), [coach, stepper, leadId]);
  const chat = useMemo(() => ({
    laden: () => coach.get(`/kunden/${leadId}/chat`),
    senden: (text) => coach.post(`/kunden/${leadId}/chat`, { text }),
    gelesen: () => coach.post(`/kunden/${leadId}/chat/gelesen`),
  }), [coach, leadId]);
  const checkins = useMemo(() => ({
    laden: () => coach.get(`/kunden/${leadId}/checkins`),
    antworten: (cid, text) => coach.post(`/coaching/checkins/${cid}/antwort`, { text }),
  }), [coach, leadId]);
  // Integration W2: Fortschritt (W2.4) und Check-in-Inbox (W2.5) über die Trainer-Sitzung — Clients stabil (useMemo)
  const fortschrittClient = useMemo(() => coachFortschrittClient(coach), [coach]);
  const rhythmus = useMemo(() => rhythmusClient(coach), [coach]);
  const inboxBasis = useMemo(() => ({ ansicht: 'kunde', kunde: leadId, reiter: 'checkins' }), [leadId]);

  const checkupStarten = () => {
    const cid = akte?.checkup_start?.checkup_id;
    if (!cid) { toast.message('Kein bestätigter Check-up-Termin. Termine gibt das Studio im CRM-Kalender frei; in der Trainer-App übernimmst du einen freien Check-up.'); return; }
    if (!zugang.checkup_trainer) { toast.error('Dein Zugang ist mit keinem Trainer-Profil verknüpft – Check-ups führst du über die Trainer-App oder das Studio-Tablet, bis die Leitung ihn verknüpft.'); return; }
    setStepper(cid);
  };
  const angebot = () => setAngebotOffen(true);
  const aktion = (a) => {
    if (a === 'checkup') checkupStarten();
    else if (a === 'coaching') wechsel('vertraege');
    else if (a === 'chat') wechsel('chat');
    else if (a === 'angebot') { if (akte?.angebote_offen?.length) wechsel('vertraege'); else angebot(); }
  };
  const zurueck = () => { if (window.history.length > 1) navigate(-1); else gehe({ ansicht: 'athleten' }); };
  // Plan öffnen → Editor (W3.1) auf /coach › Programme
  const onPlan = (zid) => gehe({ ansicht: 'programme', zyklus: zid, editor: '1' });
  const ohneErnaehrung = <Hinweis>Ernährung und Messwerte sind für deinen Trainer-Zugang bei dieser Person nicht freigeschaltet (Schalter „Ernährung“ im CRM, beim Personal Trainer nur für zugeordnete Kunden).</Hinweis>;

  if (fehler && !akte) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" className="-ml-2 gap-1.5 text-muted-foreground" onClick={zurueck}><ArrowLeft className="h-4 w-4" /> Zurück</Button>
        <Card><CardContent className="py-8 text-center text-sm text-destructive" role="alert">{fehler}</CardContent></Card>
      </div>
    );
  }

  return (
    <LinkUmleitung gehe={gehe}>
      <div className="space-y-4" data-testid="coach-kundenakte">
        {stepper && stepperClient && (
          <CheckupStepperPanel leadId={leadId} checkupId={stepper} client={stepperClient} onClose={() => { setStepper(null); laden(); }} />
        )}
        <AngebotErstellen leadId={leadId} offen={angebotOffen} onClose={() => setAngebotOffen(false)} onGesendet={laden} client={coach} basis="/kunden" />
        <Button variant="ghost" size="sm" className="-ml-2 gap-1.5 text-muted-foreground" onClick={zurueck}><ArrowLeft className="h-4 w-4" /> Zurück</Button>
        {!akte ? <Skeleton className="h-28 w-full rounded-[14px]" /> : (
          <AkteKopfCoach lead={{ name: akte.kopf?.name || '', mitgliedsnummer: akte.kopf?.mitgliedsnummer }} akte={akte} chips={<Chips chips={akte.kopf?.chips} inline />}
            onCheckup={checkupStarten} onNachricht={() => wechsel('chat')} onAngebot={angebot}
            onTermin={() => gehe({ ansicht: 'kalender', kunde: leadId })} />
        )}

        <div className="flex gap-0.5 overflow-x-auto border-b border-border" role="tablist" aria-label="Kundenakte">
          {REITER.map(([id, l]) => (
            <button key={id} type="button" role="tab" aria-selected={aktiv === id} onClick={() => wechsel(id)} data-testid={`coach-akte-reiter-${id}`}
              className={`-mb-px min-h-11 shrink-0 whitespace-nowrap border-b-2 px-3.5 py-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${aktiv === id ? 'border-marke font-extrabold text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{l}</button>
          ))}
        </div>

        {aktiv === 'uebersicht' && (
          <KarteSchutz titel="Übersicht" schluessel={leadId}>
            {akte
              ? <CoachAkteUebersicht coach={coach} leadId={leadId} akte={akte} plan={plan} aktiv={mitErnaehrung} onAktion={aktion}
                  onPlaene={() => wechsel('training')} onProfil={() => wechsel('profil')} client={coach} basis="/kunden" />
              : fehler ? <p className="text-sm text-muted-foreground" data-testid="akte-fehler">Übersicht nicht geladen: {fehler}</p> : null}
          </KarteSchutz>
        )}
        {aktiv === 'training' && (
          <div className="space-y-4" data-testid="coach-akte-training">
            {/* Integration W3: Personen-Pläne (Standard-/Zusatzplan, Auswahl in der App, Programm mit Phasen) stehen hier
                immer — auch ohne Kraftlog (neuer Kunde). Rechte wie im Editor-Paket: PT nur eigene Kunden, der Server sagt 403. */}
            <KarteSchutz titel="Trainingspläne" schluessel={`tp-${leadId}`}>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2"><PlanNeu key={`n-${leadId}`} client={coach} basis={PLANER} person={{ id: leadId, name: akte?.kopf?.name || '' }} onAngelegt={onPlan} /></div>
                <PersonenPlaene key={`p-${leadId}`} client={coach} basis={PLANER} leadId={leadId} onOeffnen={onPlan} />
              </div>
            </KarteSchutz>
            {Training
              ? <KarteSchutz titel="Training" schluessel={leadId}><Training key={leadId} mid={leadId} eingebettet ohnePlaene gehe={gehe} onPlan={onPlan} onZurueck={zurueck} /></KarteSchutz>
              : <Hinweis>Training ist hier nicht verfügbar.</Hinweis>}
          </div>
        )}
        {aktiv === 'ernaehrung' && (mitErnaehrung
          ? <KarteSchutz titel="Ernährung" schluessel={leadId}><KundenSeite key={leadId} coach={coach} ich={ich} mid={leadId} reiter={ereiter || 'uebersicht'} gehe={gehe} eingebettet /></KarteSchutz>
          : akte ? ohneErnaehrung : <Skeleton className="h-40 w-full rounded-[14px]" />)}
        {aktiv === 'checkins' && (
          <div className="space-y-4" data-testid="coach-akte-checkins">
            {checkin ? (
              <KarteSchutz titel="Check-in" schluessel={checkin}>
                <CoachCheckins coach={coach} leadId={leadId} basis={inboxBasis} checkin={checkin} stand={stand} gehe={gehe} onGeaendert={laden} />
              </KarteSchutz>
            ) : (
              <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <KarteSchutz titel="Check-ins" schluessel={leadId}>
                  <CoachCheckins coach={coach} leadId={leadId} basis={inboxBasis} stand={stand} gehe={gehe} onGeaendert={laden} />
                </KarteSchutz>
                <KarteSchutz titel="Rhythmus" schluessel={`r-${leadId}`}><CheckinRhythmusKarte leadId={leadId} api={rhythmus} /></KarteSchutz>
              </div>
            )}
            {!checkin && (
              <details className="rounded-[14px] border border-border/70 bg-card p-4" data-testid="coach-akte-checkins-alle">
                <summary className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-bold"><ClipboardList className="h-4 w-4 text-muted-foreground" aria-hidden="true" /> Alle Check-ins und Formulare</summary>
                <div className="mt-3 space-y-4">
                  <div className="text-sm font-bold">P1 Coaching · Wochen-Check-ins</div>
                  <KarteSchutz titel="Check-ins" schluessel={`a-${leadId}`}><CoachingCheckins laden={checkins.laden} antworten={checkins.antworten} /></KarteSchutz>
                  <div className="text-sm font-bold">Ernährung · Formulare und Check-ins</div>
                  {mitErnaehrung ? <KarteSchutz titel="Formulare" schluessel={`f-${leadId}`}><Formulare coach={coach} mid={leadId} /></KarteSchutz> : ohneErnaehrung}
                </div>
              </details>
            )}
          </div>
        )}
        {aktiv === 'fortschritt' && (
          <div className="space-y-3" data-testid="coach-akte-fortschritt">
            {/* Wie im CRM: mit Ernährungs-Freigabe die Unterteilung „Fortschritt | Entwicklung & Check-ins“ — die Entwicklung
                kann Dinge, die der Fortschritt nicht hat (Messwerte bearbeiten, Check-in-Karte, Aktivitäten). */}
            {mitErnaehrung && (
              <div className="flex gap-1" role="tablist" aria-label="Ansicht Fortschritt">
                {[['fortschritt', 'Fortschritt'], ['entwicklung', 'Entwicklung & Check-ins']].map(([w, n]) => (
                  <button key={w} type="button" role="tab" aria-selected={(ereiter || 'fortschritt') === w} onClick={() => wechsel('fortschritt', w === 'fortschritt' ? {} : { ereiter: w })}
                    data-testid={`coach-akte-fortschritt-${w}`}
                    className={`min-h-11 rounded-full border px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${(ereiter || 'fortschritt') === w ? 'border-foreground bg-foreground text-background' : 'border-border hover:bg-muted'}`}>{n}</button>
                ))}
              </div>
            )}
            {(!mitErnaehrung || ereiter !== 'entwicklung')
              ? <KarteSchutz titel="Fortschritt" schluessel={leadId}><KundeFortschritt key={leadId} leadId={leadId} client={fortschrittClient} fuerTrainer coachStil /></KarteSchutz>
              : <KarteSchutz titel="Entwicklung" schluessel={`e-${leadId}`}><KundeEntwicklung leadId={leadId}
                  onProtokoll={() => wechsel('ernaehrung', { ereiter: 'protokoll' })} onChat={() => wechsel('chat')} onSprachnotiz={() => wechsel('chat')} /></KarteSchutz>}
            {akte && !zugang.ernaehrung && <Hinweis>Gewicht, Umfänge, Ernährung und Check-in-Treue zeigt der Fortschritt nur mit Ernährungs-Freigabe für diese Person; Training und Heatmap immer.</Hinweis>}
          </div>
        )}
        {aktiv === 'chat' && (mitErnaehrung
          ? <KarteSchutz titel="Chat" schluessel={leadId}><div className="flex min-h-[28rem] flex-col rounded-[14px] border border-border/70 bg-card"><CoachingKonversation leadId={leadId} name={akte?.kopf?.name || ''} onGeaendert={laden} onZurEntwicklung={() => wechsel('fortschritt', { ereiter: 'entwicklung' })} /></div></KarteSchutz>
          : <KarteSchutz titel="Chat" schluessel={leadId}><CoachChat key={`c-${leadId}`} {...chat} testId="coach-akte-chat" /></KarteSchutz>)}
        {aktiv === 'vertraege' && <Vertraege key={`${leadId}-${angebotOffen}`} coach={coach} leadId={leadId} onAngebot={angebot} />}
        {aktiv === 'profil' && <Profil coach={coach} leadId={leadId} />}
        {!akte && !fehler && aktiv === 'uebersicht' && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> lädt …</div>}
        {akte && !zugang.ernaehrung && aktiv === 'uebersicht' && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><UserRound className="h-3.5 w-3.5" aria-hidden="true" /> Messwerte und Ernährungsdaten zeigt die Akte nur mit Ernährungs-Freigabe für diese Person.</p>
        )}
      </div>
    </LinkUmleitung>
  );
}
